export const PLATFORM_ADMIN_BASE = '/platform-admin';

export type AdminTab =
  | 'store'
  | 'storeGroups'
  | 'user'
  | 'assistant'
  | 'adminOrders'
  | 'adminCalls'
  | 'adminReservations'
  | 'insights'
  | 'refundReview'
  | 'integrations'
  | 'knowledgeBases'
  | 'faqSupport'
  | 'posConfig'
  | 'tsv'
  | 'onboardingBilling'
  | 'notifications'
  | 'vipContacts'
  | 'demoLines'
  | 'edit';

export type OnboardingBillingSubtab =
  | 'intakes'
  | 'offers'
  | 'plans'
  | 'financials'
  | 'subscriptions'
  | 'status';

export type TwilioConfigSubtab =
  | 'phoneNumbers'
  | 'tollFree'
  | 'secondaryProfiles';

export type PlansSubTab = 'catalog' | 'override';
export type IntegrationSubTab = 'manual';
export type PosConfigSubtab = 'poshub';
export type NotificationSubTab = 'subscriptions' | 'sent';

export type PlatformAdminRouteState = {
  tab: AdminTab;
  onboardingSubtab: OnboardingBillingSubtab;
  twilioSubtab: TwilioConfigSubtab;
  plansSubtab: PlansSubTab;
  integrationSubtab: IntegrationSubTab;
  posSubtab: PosConfigSubtab;
  notificationsSubtab: NotificationSubTab;
};

const TAB_STORAGE_KEY = 'platformAdminTab';
const ONBOARDING_STORAGE_KEY = 'platformAdminOnboardingBillingSubtab';
const TWILIO_STORAGE_KEY = 'platformAdminTwilioConfigSubtab';
const PLANS_STORAGE_KEY = 'platformAdminPlansSubtab';
const NOTIFICATIONS_STORAGE_KEY = 'platformAdminNotificationsSubtab';

const MODEL_TAB_TO_SLUG: Record<string, string> = {
  store: 'stores',
  storeGroups: 'store-groups',
  user: 'users',
  assistant: 'agents',
  adminOrders: 'orders',
  adminCalls: 'calls',
  adminReservations: 'reservations',
  edit: 'edit-store',
};

const SECTION_TAB_TO_SLUG: Record<string, string> = {
  insights: 'insights',
  refundReview: 'refund-review',
  integrations: 'integrations',
  knowledgeBases: 'knowledge-bases',
  faqSupport: 'faq-support',
  posConfig: 'pos-config',
  tsv: 'twilio',
  onboardingBilling: 'onboarding-billing',
  notifications: 'notifications',
  vipContacts: 'vip-contacts',
  demoLines: 'demo-lines',
};

const ONBOARDING_SLUGS: Record<string, OnboardingBillingSubtab> = {
  intakes: 'intakes',
  offers: 'offers',
  plans: 'plans',
  financials: 'financials',
  subscriptions: 'subscriptions',
  status: 'status',
};

const TWILIO_SLUGS: Record<string, TwilioConfigSubtab> = {
  'phone-numbers': 'phoneNumbers',
  'toll-free': 'tollFree',
  'secondary-profiles': 'secondaryProfiles',
};

const TWILIO_TAB_TO_SLUG: Record<TwilioConfigSubtab, string> = {
  phoneNumbers: 'phone-numbers',
  tollFree: 'toll-free',
  secondaryProfiles: 'secondary-profiles',
};

const NOTIFICATION_SLUGS: Record<string, NotificationSubTab> = {
  subscriptions: 'subscriptions',
  sent: 'sent',
};

const NOTIFICATION_TAB_TO_SLUG: Record<NotificationSubTab, string> = {
  subscriptions: 'subscriptions',
  sent: 'sent',
};

const MODEL_SLUG_TO_TAB = Object.fromEntries(
  Object.entries(MODEL_TAB_TO_SLUG).map(([tab, slug]) => [slug, tab]),
) as Record<string, AdminTab>;

const SECTION_SLUG_TO_TAB = Object.fromEntries(
  Object.entries(SECTION_TAB_TO_SLUG).map(([tab, slug]) => [slug, tab]),
) as Record<string, AdminTab>;

export const DEFAULT_PLATFORM_ADMIN_ROUTE: PlatformAdminRouteState = {
  tab: 'store',
  onboardingSubtab: 'offers',
  twilioSubtab: 'phoneNumbers',
  plansSubtab: 'catalog',
  integrationSubtab: 'manual',
  posSubtab: 'poshub',
  notificationsSubtab: 'subscriptions',
};

const ADMIN_TABS = new Set<string>([
  ...Object.keys(MODEL_TAB_TO_SLUG),
  ...Object.keys(SECTION_TAB_TO_SLUG),
]);

const isModelTab = (tab: string) => tab in MODEL_TAB_TO_SLUG;

const readStorage = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Session storage can be unavailable in private contexts.
  }
};

export const readPlatformAdminFallback = (): PlatformAdminRouteState => {
  const savedTab = readStorage(TAB_STORAGE_KEY);
  const savedOnboarding = readStorage(ONBOARDING_STORAGE_KEY);
  const savedTwilio = readStorage(TWILIO_STORAGE_KEY);
  const savedPlans = readStorage(PLANS_STORAGE_KEY);
  const savedNotifications = readStorage(NOTIFICATIONS_STORAGE_KEY);

  return {
    ...DEFAULT_PLATFORM_ADMIN_ROUTE,
    tab:
      savedTab && ADMIN_TABS.has(savedTab)
        ? (savedTab as AdminTab)
        : DEFAULT_PLATFORM_ADMIN_ROUTE.tab,
    onboardingSubtab:
      savedOnboarding && savedOnboarding in ONBOARDING_SLUGS
        ? (savedOnboarding as OnboardingBillingSubtab)
        : DEFAULT_PLATFORM_ADMIN_ROUTE.onboardingSubtab,
    twilioSubtab:
      savedTwilio &&
      (Object.values(TWILIO_SLUGS) as string[]).includes(savedTwilio)
        ? (savedTwilio as TwilioConfigSubtab)
        : DEFAULT_PLATFORM_ADMIN_ROUTE.twilioSubtab,
    plansSubtab:
      savedPlans === 'override' || savedPlans === 'catalog'
        ? savedPlans
        : DEFAULT_PLATFORM_ADMIN_ROUTE.plansSubtab,
    notificationsSubtab:
      savedNotifications === 'sent' || savedNotifications === 'subscriptions'
        ? savedNotifications
        : DEFAULT_PLATFORM_ADMIN_ROUTE.notificationsSubtab,
  };
};

export const writePlatformAdminSession = (state: PlatformAdminRouteState) => {
  writeStorage(TAB_STORAGE_KEY, state.tab);
  writeStorage(ONBOARDING_STORAGE_KEY, state.onboardingSubtab);
  writeStorage(TWILIO_STORAGE_KEY, state.twilioSubtab);
  writeStorage(PLANS_STORAGE_KEY, state.plansSubtab);
  writeStorage(NOTIFICATIONS_STORAGE_KEY, state.notificationsSubtab);
};

export const buildPlatformAdminPath = (
  state: Partial<PlatformAdminRouteState> & { tab: AdminTab },
) => {
  const tab = state.tab;
  if (isModelTab(tab)) {
    return `${PLATFORM_ADMIN_BASE}/models/${MODEL_TAB_TO_SLUG[tab]}`;
  }
  if (tab === 'integrations') {
    return `${PLATFORM_ADMIN_BASE}/integrations/${
      state.integrationSubtab || 'manual'
    }`;
  }
  if (tab === 'posConfig') {
    return `${PLATFORM_ADMIN_BASE}/pos-config/${state.posSubtab || 'poshub'}`;
  }
  if (tab === 'tsv') {
    const subtab = state.twilioSubtab || 'phoneNumbers';
    return `${PLATFORM_ADMIN_BASE}/twilio/${TWILIO_TAB_TO_SLUG[subtab]}`;
  }
  if (tab === 'notifications') {
    const subtab = state.notificationsSubtab || 'subscriptions';
    return `${PLATFORM_ADMIN_BASE}/notifications/${NOTIFICATION_TAB_TO_SLUG[subtab]}`;
  }
  if (tab === 'onboardingBilling') {
    const subtab = state.onboardingSubtab || 'offers';
    if (subtab === 'plans' && state.plansSubtab === 'override') {
      return `${PLATFORM_ADMIN_BASE}/onboarding-billing/plans/overrides`;
    }
    return `${PLATFORM_ADMIN_BASE}/onboarding-billing/${subtab}`;
  }
  return `${PLATFORM_ADMIN_BASE}/${SECTION_TAB_TO_SLUG[tab]}`;
};

const normalizePath = (pathname: string) => {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
};

const withParsed = (
  fallback: PlatformAdminRouteState,
  patch: Partial<PlatformAdminRouteState>,
): PlatformAdminRouteState => ({
  ...fallback,
  ...patch,
});

export const resolvePlatformAdminRoute = (
  pathname: string,
  fallback: PlatformAdminRouteState = DEFAULT_PLATFORM_ADMIN_ROUTE,
): { state: PlatformAdminRouteState; redirectTo: string | null } => {
  const normalized = normalizePath(pathname);
  const fallbackPath = buildPlatformAdminPath(fallback);
  if (
    normalized !== PLATFORM_ADMIN_BASE &&
    !normalized.startsWith(`${PLATFORM_ADMIN_BASE}/`)
  ) {
    return { state: fallback, redirectTo: fallbackPath };
  }

  const rest =
    normalized === PLATFORM_ADMIN_BASE
      ? ''
      : normalized.slice(PLATFORM_ADMIN_BASE.length + 1);
  const [section, sub, detail, ...extra] = rest.split('/').filter(Boolean);

  if (!section || extra.length > 0) {
    return { state: fallback, redirectTo: fallbackPath };
  }

  let state: PlatformAdminRouteState | null = null;

  if (section === 'models') {
    const tab = sub ? MODEL_SLUG_TO_TAB[sub] : undefined;
    if (!sub) {
      state = withParsed(fallback, {
        tab: isModelTab(fallback.tab) ? fallback.tab : 'store',
      });
    } else if (tab && !detail) {
      state = withParsed(fallback, { tab });
    }
  } else if (section === 'integrations') {
    if (!sub) {
      state = withParsed(fallback, {
        tab: 'integrations',
        integrationSubtab: 'manual',
      });
    } else if (sub === 'manual' && !detail) {
      state = withParsed(fallback, {
        tab: 'integrations',
        integrationSubtab: 'manual',
      });
    }
  } else if (section === 'pos-config') {
    if (!sub) {
      state = withParsed(fallback, { tab: 'posConfig', posSubtab: 'poshub' });
    } else if (sub === 'poshub' && !detail) {
      state = withParsed(fallback, { tab: 'posConfig', posSubtab: 'poshub' });
    }
  } else if (section === 'twilio') {
    if (!sub) {
      state = withParsed(fallback, { tab: 'tsv' });
    } else if (TWILIO_SLUGS[sub] && !detail) {
      state = withParsed(fallback, {
        tab: 'tsv',
        twilioSubtab: TWILIO_SLUGS[sub],
      });
    }
  } else if (section === 'notifications') {
    if (!sub) {
      state = withParsed(fallback, { tab: 'notifications' });
    } else if (NOTIFICATION_SLUGS[sub] && !detail) {
      state = withParsed(fallback, {
        tab: 'notifications',
        notificationsSubtab: NOTIFICATION_SLUGS[sub],
      });
    }
  } else if (section === 'onboarding-billing') {
    if (!sub) {
      state = withParsed(fallback, { tab: 'onboardingBilling' });
    } else if (ONBOARDING_SLUGS[sub]) {
      if (sub === 'plans' && !detail) {
        state = withParsed(fallback, {
          tab: 'onboardingBilling',
          onboardingSubtab: 'plans',
          plansSubtab: 'catalog',
        });
      } else if (sub === 'plans' && detail === 'overrides') {
        state = withParsed(fallback, {
          tab: 'onboardingBilling',
          onboardingSubtab: 'plans',
          plansSubtab: 'override',
        });
      } else if (sub === 'plans' && detail === 'catalog') {
        state = withParsed(fallback, {
          tab: 'onboardingBilling',
          onboardingSubtab: 'plans',
          plansSubtab: 'catalog',
        });
      } else if (sub !== 'plans' && !detail) {
        state = withParsed(fallback, {
          tab: 'onboardingBilling',
          onboardingSubtab: ONBOARDING_SLUGS[sub],
        });
      }
    }
  } else if (SECTION_SLUG_TO_TAB[section] && !sub) {
    state = withParsed(fallback, { tab: SECTION_SLUG_TO_TAB[section] });
  }

  if (!state) {
    return { state: fallback, redirectTo: fallbackPath };
  }

  const canonical = buildPlatformAdminPath(state);
  return {
    state,
    redirectTo: canonical === normalized ? null : canonical,
  };
};
